'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLegacyTrackNavigation } from './useLegacyTrackNavigation';
//import { useTrackNavigation } from './useTrackNavigation';

export const useRouteTracking = () => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { trackLegacyNavigation } = useLegacyTrackNavigation();
  //const { trackNavigation } = useTrackNavigation();

  const previousRouteRef = useRef<string | null>(null);
  const isInitialLoadRef = useRef(true);
  const isPopstateRef = useRef(false);

  // Memoize current route construction (excludes hash for clean analytics)
  const currentRoute = useMemo(() => {
    const params = searchParams.toString();
    return params ? `${pathname}?${params}` : pathname;
  }, [pathname, searchParams]);

  // Stable tracking function
  const handleRouteChange = useCallback(
    (from: string | undefined, to: string, _method: 'browser' | 'click') => {
      trackLegacyNavigation({ from, path: to });
      //trackNavigation({ from, to, method });
    },
    [trackLegacyNavigation],
  );

  // Track route changes
  useEffect(() => {
    if (isInitialLoadRef.current) {
      // Track initial page load (from external referrer only, not internal navigation)
      const referrer =
        document.referrer && !document.referrer.includes(window.location.origin)
          ? document.referrer
          : undefined;
      handleRouteChange(referrer, currentRoute, 'browser');
      isInitialLoadRef.current = false;
    } else {
      // Track route changes (only if route actually changed)
      if (previousRouteRef.current !== currentRoute) {
        // Use the flag to determine if this was browser navigation
        const method = isPopstateRef.current ? 'browser' : 'click';
        handleRouteChange(previousRouteRef.current || undefined, currentRoute, method);

        // Reset the flag after use
        isPopstateRef.current = false;
      }
    }

    previousRouteRef.current = currentRoute;
  }, [currentRoute, handleRouteChange]);

  // Listen for browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      // Set flag to indicate next route change is from browser navigation
      isPopstateRef.current = true;
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);
};
